import {
  PERMISSION_INHERITANCE,
  PermissionLevel,
  ResourceType,
  RoomSubType,
  UserSubType,
} from '@models';

export const canManage = (permission: number): boolean =>
  permission === PermissionLevel.MODIFY_AND_DELETE;

export const canAccess = (permission: number): boolean =>
  permission >= PermissionLevel.ACCESS;

export const canDeleteResource = (
  resource: { permission: number; authorId?: number },
  user: { userId?: number; editorDeletePermission: boolean }
): boolean =>
  canManage(resource.permission) ||
  (resource.permission === PermissionLevel.CONTRIBUTE &&
    resource.authorId === user.userId &&
    user.editorDeletePermission);

export const isNotGuest = (user: { guest?: boolean }): boolean =>
  user.guest !== true;

export const getDisplayPermissionLevel = (
  level: PermissionLevel,
  inheritance?: PERMISSION_INHERITANCE | number
): PermissionLevel =>
  level === PermissionLevel.CONTRIBUTE &&
  inheritance === PERMISSION_INHERITANCE.NONE
    ? PermissionLevel.UPLOAD
    : level;

export const getStoragePermission = (
  level: PermissionLevel
): { level: PermissionLevel; inheritance?: PERMISSION_INHERITANCE } =>
  level === PermissionLevel.UPLOAD
    ? {
        level: PermissionLevel.CONTRIBUTE,
        inheritance: PERMISSION_INHERITANCE.NONE,
      }
    : { level };

export const isWritableResource = (
  destination: {
    permission: number;
    type: ResourceType;
    parentSubtype?: number;
  },
  resourceType: ResourceType
) => {
  if (
    destination.permission > PermissionLevel.ACCESS &&
    destination.type !== ResourceType.FILE
  ) {
    if (
      destination.parentSubtype === RoomSubType.ROOMS_ROOT &&
      destination.type === ResourceType.ROOM &&
      resourceType === ResourceType.ROOM
    ) {
      return false;
    } else if (destination.parentSubtype !== RoomSubType.ROOMS_ROOT) {
      return false;
    }
  } else if (destination.type === ResourceType.FILE) {
    return false;
  }

  return true;
};

export const isEditable = (
  obj: { permission?: number } | { permission?: number }[]
) =>
  !(Array.isArray(obj) ? obj : [obj]).some(
    (item) =>
      item.permission === undefined ||
      item.permission < PermissionLevel.CONTRIBUTE
  );

export const viewersByList = (list: { permission: number }[]) =>
  !!list.find((el) => canAccess(el.permission));

export const ownersByList = (list: { permission: number }[]) =>
  !!list.find((el) => canManage(el.permission));

export const deleteEnablebyList = (
  list: {
    permission: number;
    authorId?: number;
    type: ResourceType;
    subtype?: number;
  }[],
  userContext: { userId?: number; editorDeletePermission: boolean }
) =>
  !!list.find(
    (el) =>
      canDeleteResource(el, userContext) &&
      !(el.type === ResourceType.USER && el.subtype === UserSubType.AZURE_AD)
  );

export const isOnlyFormatList = <
  T extends { format?: string; secView?: boolean; secViewError?: number },
>(
  list: readonly T[],
  format: string,
  isNotPasswordProtected: boolean
) =>
  !list.some(
    (el) =>
      el.format !== format ||
      (isNotPasswordProtected && (!el.secView || el.secViewError === 4))
  );

export const downloadEnablebyList = (
  list: { permission: number; type: ResourceType }[]
) => {
  const allowed = list.filter(
    (el) => el.permission >= PermissionLevel.SEC_PRINT
  );

  const hasForbiddenType = list.some((el) =>
    [
      ResourceType.USER,
      ResourceType.GROUP,
      ResourceType.DISCUSSION,
      ResourceType.VOTING,
    ].includes(el.type)
  );

  return !hasForbiddenType && allowed.length === list.length;
};

export const exportEnablebyList = (list: { permission: number }[]) =>
  list.filter((el) => el.permission >= PermissionLevel.SEC_PRINT).length ===
  list.length;
